import Textfield from '@atlaskit/textfield';
import {
  EditableListWithPlaceholder,
  type EditableListProps,
} from '@databases/editable-list';
import { submitFormOnEnter } from '@databases/form/Form';
import { SeamlessTextfield } from '@databases/form/styled';
import { useTextDelta } from '@databases/react/useTextDelta';
import { DatabaseFieldType, type RawDatabaseValue } from '@databases/types/database';
import { MultiValueChangeType } from '@databases/utils/database/changesets';
import { isValidHyperlinkUrl } from '@databases/utils/utils';
import { generateId } from '@databases/utils/utils';
import { noop } from 'lodash-es';
import { type Op } from 'quill-delta';
import React, { type FunctionComponent, useCallback, useState } from 'react';

import { Container } from './TextValueEdit';
import { type EditComponentProps } from './types';

type Value = RawDatabaseValue<DatabaseFieldType.HYPERLINK>[0];

const getId = (value: Value): string => value._id;

const getNewValue = (): Value => ({ _id: generateId(), url: '' });

const HyperlinkValueEdit: FunctionComponent<
  EditComponentProps<DatabaseFieldType.HYPERLINK>
> = ({ onChange, onFocus, onBlur, autoFocus, value, field, isSeamless, isInvalid }) => {
  const isMulti = field.settings.multi || value.length > 1;

  const [textareaRef, setTextareaRef] = useState<HTMLTextAreaElement | null>(null);
  useTextDelta(
    textareaRef,
    useCallback(
      (delta, newValue) => {
        const singleLink: RawDatabaseValue<DatabaseFieldType.HYPERLINK>[0] =
          value.length === 0
            ? { _id: generateId(), url: newValue }
            : { _id: value[0]._id, url: newValue };
        onChange([singleLink], {
          type: DatabaseFieldType.HYPERLINK,
          delta:
            value.length === 0
              ? {
                  type: MultiValueChangeType.APPEND,
                  value: singleLink,
                }
              : {
                  type: MultiValueChangeType.EDIT,
                  index: 0,
                  delta,
                },
        });
      },
      [onChange, value]
    )
  );

  const [inputValue, setInputValue] = useState<Value[]>(value);

  const renderField: EditableListProps<Value, Op[]>['renderField'] = useCallback(
    ({ value, autoFocus, onChange }) => {
      const [multiTextareaRef, setMultiTextareaRef] =
        // eslint-disable-next-line react-hooks/rules-of-hooks
        useState<HTMLTextAreaElement | null>(null);
      // eslint-disable-next-line react-hooks/rules-of-hooks
      useTextDelta(
        multiTextareaRef,
        // eslint-disable-next-line react-hooks/rules-of-hooks
        useCallback(
          (delta, newValue) => {
            onChange({ _id: value._id, url: newValue }, delta);
          },
          [onChange, value]
        )
      );

      return (
        <SeamlessTextfield
          value={value.url}
          onChange={noop}
          ref={setMultiTextareaRef}
          autoFocus={autoFocus}
          isInvalid={!!value.url && !isValidHyperlinkUrl(value.url)}
        />
      );
    },
    []
  );

  const onItemAdd = React.useCallback(
    (val: Value, allValues: Value[]) => {
      setInputValue(allValues);

      onChange(allValues, {
        type: DatabaseFieldType.HYPERLINK,
        delta: {
          type: MultiValueChangeType.APPEND,
          value: val,
        },
      });
    },
    [onChange]
  );

  const onItemDelete = React.useCallback(
    (val: Value, removeIndex: number, allValues: Value[]) => {
      setInputValue(allValues);

      onChange(allValues, {
        type: DatabaseFieldType.HYPERLINK,
        delta: {
          type: MultiValueChangeType.REMOVE,
          index: removeIndex,
        },
      });
    },
    [onChange]
  );

  const onItemChange = React.useCallback(
    (val: Value, newIndex: number, delta: Op[], allValues: Value[]) => {
      setInputValue(allValues);

      onChange(allValues, {
        type: DatabaseFieldType.HYPERLINK,
        delta: {
          type: MultiValueChangeType.EDIT,
          index: newIndex,
          delta,
        },
      });
    },
    [onChange]
  );

  const onItemReorder = React.useCallback(
    (val: Value, currentIndex: number, newIndex: number, allValues: Value[]) => {
      setInputValue(allValues);

      onChange(allValues, {
        type: DatabaseFieldType.HYPERLINK,
        delta: {
          type: MultiValueChangeType.REORDER,
          currentIndex,
          newIndex,
        },
      });
    },
    [onChange]
  );

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLTextAreaElement>) => submitFormOnEnter(e),
    []
  );

  if (isMulti) {
    return (
      <EditableListWithPlaceholder<Value, Op[]>
        getId={getId}
        values={inputValue}
        onItemChange={onItemChange}
        onAddNewItem={onItemAdd}
        onRemoveItem={onItemDelete}
        onReorder={onItemReorder}
        getNewValue={getNewValue}
        renderField={renderField}
        autoFocus={value.length === 0 ? autoFocus : undefined}
        addLabel="Add link"
        noAdd={!field.settings.multi}
        onFocus={onFocus}
        onBlur={onBlur}
      />
    );
  } else {
    const Component = isSeamless ? SeamlessTextfield : Textfield;
    const textValue = value.length > 0 ? value[0].url : '';
    return (
      <Container>
        <Component
          value={textValue}
          onChange={noop}
          onFocus={onFocus}
          onBlur={onBlur}
          onKeyDown={handleKeyDown}
          autoFocus={autoFocus}
          isInvalid={isInvalid}
          ref={setTextareaRef}
        />
      </Container>
    );
  }
};

export default HyperlinkValueEdit;
